/**
 * Optimized collection load-more:
 * - Prefetch next page on mount and on hover
 * - Lightweight section response (load_more_only=1)
 * - CSS-only loading animation (isolated from main-thread DOM work)
 * - Single DOM insert (no staggered card appearance)
 * - Viewport-lazy carousels
 * - When page-size is set, keep fetching until that many product cards can be
 *   shown, then hold any extras until the next load-more. Used after makeup
 *   shades collapse to one card.
 */

const pageCache = new Map();
const pendingFetches = new Map();
const POINTER_CLICK_TOLERANCE_PX = 12;

function extractSectionId(element) {
  const section = element.closest(".shopify-section");
  return section?.id.replace("shopify-section-", "") ?? "";
}

function buildSectionUrl(pageHref, sectionId) {
  const url = new URL(pageHref, window.location.href);
  url.searchParams.set("section_id", sectionId);
  url.searchParams.set("load_more_only", "1");
  return url.toString();
}

function buildFetchUrl(element) {
  return buildSectionUrl(
    element.getAttribute("next-url"),
    extractSectionId(element),
  );
}

function collectionPageHref(page) {
  const url = new URL(window.location.href);
  url.searchParams.delete("page");
  url.searchParams.delete("section_id");
  url.searchParams.delete("load_more_only");
  if (page > 1) {
    url.searchParams.set("page", String(page));
  }
  return `${url.pathname}${url.search}`;
}

function countProductCards(nodes) {
  let count = 0;

  for (const node of nodes) {
    if (node.nodeType === 1 && node.matches("product-card")) {
      count += 1;
    }
  }

  return count;
}

function shownCardCount(productList) {
  let count = 0;

  for (const node of productList.children) {
    if (
      node.nodeType === 1 &&
      node.matches("product-card") &&
      !node.hasAttribute("data-style-held")
    ) {
      count += 1;
    }
  }

  return count;
}

function visibleCardCount(productList) {
  return shownCardCount(productList);
}

function heldCardCount(productList) {
  let count = 0;

  for (const node of productList.children) {
    if (
      node.nodeType === 1 &&
      node.matches("product-card") &&
      node.hasAttribute("data-style-held")
    ) {
      count += 1;
    }
  }

  return count;
}

function holdNode(node) {
  node.setAttribute("data-style-held", "");
  node.hidden = true;
  node.style.display = "none";
}

function releaseNode(node) {
  node.removeAttribute("data-style-held");
  node.hidden = false;
  node.style.display = "";
}

function applyShownLimit(productList, limit) {
  let shown = 0;

  for (const node of productList.children) {
    if (node.nodeType !== 1) {
      continue;
    }

    if (node.matches("product-card") && shown < limit) {
      shown += 1;
      releaseNode(node);
      continue;
    }

    if (shown >= limit) {
      holdNode(node);
    } else {
      releaseNode(node);
    }
  }
}

function loadMoreForList(productListId) {
  return document.querySelector(
    `collection-load-more[product-list-id="${CSS.escape(productListId)}"]`,
  );
}

function waitForPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

function prepareImportedNode(node) {
  const imported = document.importNode(node, true);
  imported.removeAttribute("reveal-on-scroll");

  if (imported.matches("product-card")) {
    imported.setAttribute("data-defer-carousel", "");
  } else {
    imported.querySelectorAll("product-card").forEach((card) => {
      card.setAttribute("data-defer-carousel", "");
    });
  }

  return imported;
}

async function fetchPageHtml(url) {
  if (pageCache.has(url)) {
    return pageCache.get(url);
  }

  let pending = pendingFetches.get(url);
  if (!pending) {
    pending = fetch(url)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const html = await response.text();
        pageCache.set(url, html);
        return html;
      })
      .finally(() => {
        pendingFetches.delete(url);
      });
    pendingFetches.set(url, pending);
  }

  return pending;
}

function parseSectionHtml(html, sectionId, productListId) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const section = doc.querySelector(
    "#shopify-section-" + CSS.escape(sectionId),
  );
  const productList = section?.querySelector("#" + CSS.escape(productListId));

  if (!section || !productList) {
    return null;
  }

  return {
    items: Array.from(productList.children),
    nextLoadMore: section.querySelector("collection-load-more"),
    endButton: section.querySelector("[data-collection-end-button]"),
  };
}

function parseResponse(element, html) {
  return parseSectionHtml(
    html,
    extractSectionId(element),
    element.getAttribute("product-list-id"),
  );
}

function syncViewingProgress(loadMore, productList) {
  const progress = loadMore.querySelector(".collection__load-more-progress");
  const template = progress?.getAttribute("data-viewing-label");

  if (!progress || !template) {
    return;
  }

  const visible = visibleCardCount(productList);
  const exactTotal = productList.getAttribute("data-style-visible-total");
  let total;

  if (exactTotal) {
    total = Math.max(visible, Number(exactTotal));
  } else {
    const catalogTotal = Number(
      progress.getAttribute("data-catalog-total") || "0",
    );
    const catalogSeen = Number(
      progress.getAttribute("data-catalog-seen") || "0",
    );
    const hidden = Math.max(0, catalogSeen - visible);
    total = Math.max(visible, catalogTotal - hidden);
  }

  progress.textContent = template
    .replace("__CURRENT__", String(visible))
    .replace("__TOTAL__", String(total));
}

function syncToolbarCount(productList, total) {
  const template = productList.getAttribute("data-products-count-label");

  if (!template || !template.includes("987654")) {
    return;
  }

  const label = template.replace("987654", String(total));
  const section = productList.closest(".shopify-section");

  const roots = [
    section,
    document.querySelector(".shopify-section--collection-products-count"),
  ];

  roots.forEach((root) => {
    root?.querySelectorAll(".collection-toolbar__products-count").forEach((node) => {
      if (node.hasAttribute("data-facet-products-count")) {
        return;
      }
      node.textContent = label;
    });
  });

  document.dispatchEvent(
    new CustomEvent("collection-products-count:sync", {
      detail: { total },
    }),
  );
}

function publishRealTotal(productList, total) {
  if (!productList || total <= 0) {
    return;
  }

  productList.setAttribute("data-style-visible-total", String(total));
  syncToolbarCount(productList, total);

  const loadMore = loadMoreForList(productList.id);
  if (!loadMore) {
    return;
  }

  syncViewingProgress(loadMore, productList);
  syncLoadMoreCount(loadMore);
}

async function countVisibleInCollection(sectionId, productListId, pageCount, firstPageCards) {
  const counts = new Array(pageCount + 1);
  counts[1] = firstPageCards;
  let cursor = 2;

  async function worker() {
    while (cursor <= pageCount) {
      const page = cursor;
      cursor += 1;
      const html = await fetchPageHtml(
        buildSectionUrl(collectionPageHref(page), sectionId),
      );
      const parsed = parseSectionHtml(html, sectionId, productListId);
      if (!parsed) {
        throw new Error("Missing style page count");
      }
      counts[page] = countProductCards(parsed.items);
    }
  }

  const workers = Math.min(4, Math.max(0, pageCount - 1));
  await Promise.all(Array.from({ length: workers }, () => worker()));

  let total = 0;
  for (let page = 1; page <= pageCount; page += 1) {
    total += counts[page];
  }
  return total;
}

function ensureVisibleTotal(loadMore, productList) {
  if (
    productList.hasAttribute("data-style-visible-total") ||
    productList._styleVisibleTotalPromise
  ) {
    return;
  }

  const progress = loadMore.querySelector(".collection__load-more-progress");
  const catalogSeen = Number(
    progress?.getAttribute("data-catalog-seen") || "0",
  );
  const catalogTotal = Number(
    progress?.getAttribute("data-catalog-total") ||
      productList.getAttribute("data-catalog-total") ||
      "0",
  );
  const visible = visibleCardCount(productList);
  const pageSize = Number(loadMore.getAttribute("page-size") || "0");
  const pending = productList.hasAttribute("data-style-count-pending");
  const hasNextPage = loadMore.hasAttribute("next-url");

  if (!hasNextPage) {
    publishRealTotal(productList, countProductCards(productList.children));
    return;
  }

  if (!pending && catalogSeen <= visible) {
    return;
  }

  const pageCount =
    pageSize > 0 && catalogTotal > 0 ? Math.ceil(catalogTotal / pageSize) : 0;
  if (pageCount < 2) {
    return;
  }

  const token = `${Date.now()}-${Math.random()}`;
  const sectionId = extractSectionId(loadMore);
  const productListId = productList.id;
  const firstPageCards = countProductCards(productList.children);

  productList.setAttribute("data-style-count-token", token);
  productList._styleVisibleTotalPromise = countVisibleInCollection(
    sectionId,
    productListId,
    pageCount,
    firstPageCards,
  )
    .then((total) => {
      const liveList = document.getElementById(productListId);

      if (
        !liveList ||
        liveList.getAttribute("data-style-count-token") !== token ||
        total <= 0
      ) {
        return;
      }

      publishRealTotal(liveList, total);
    })
    .catch((error) => {
      console.error("[collection-load-more]", error);
    });
}

async function nextVisibleBatchCount(loadMore) {
  let pageHref = loadMore.getAttribute("next-url");
  const sectionId = extractSectionId(loadMore);
  const productListId = loadMore.getAttribute("product-list-id");

  for (let guard = 0; guard < 20 && pageHref; guard += 1) {
    const html = await fetchPageHtml(buildSectionUrl(pageHref, sectionId));
    const parsed = parseSectionHtml(html, sectionId, productListId);

    if (!parsed) {
      return null;
    }

    const count = countProductCards(parsed.items);
    if (count > 0) {
      return count;
    }

    pageHref = parsed.nextLoadMore?.getAttribute("next-url") || "";
  }

  return 0;
}

async function syncLoadMoreCount(loadMore) {
  const template = loadMore.getAttribute("data-load-more-label");

  if (!template) {
    return;
  }

  const pageSize = Number(loadMore.getAttribute("page-size"));
  const productList = document.getElementById(
    loadMore.getAttribute("product-list-id"),
  );
  const held = productList ? heldCardCount(productList) : 0;

  if (!loadMore.hasAttribute("next-url")) {
    if (!pageSize || held <= 0) {
      return;
    }

    const button = loadMore.querySelector(
      "button.collection__button, button.button",
    );
    if (button) {
      button.textContent = template.replace(
        "__COUNT__",
        String(Math.min(pageSize, held)),
      );
    }
    return;
  }

  let count = null;

  if (pageSize && productList) {
    const exactTotal = Number(
      productList.getAttribute("data-style-visible-total") || "0",
    );
    const shown = shownCardCount(productList);
    if (exactTotal > 0) {
      count = Math.min(pageSize, Math.max(0, exactTotal - shown));
    } else if (held > 0) {
      count = Math.min(pageSize, held);
    } else {
      return;
    }
  } else {
    count = await nextVisibleBatchCount(loadMore);
  }

  if (count == null || !loadMore.isConnected) {
    return;
  }

  const button = loadMore.querySelector(
    "button.collection__button, button.button",
  );
  if (!button) {
    return;
  }

  button.textContent = template.replace("__COUNT__", String(count));
}

async function fillToLimit(loadMore, options = {}) {
  const listId = loadMore.getAttribute("product-list-id");
  const list = listId ? document.getElementById(listId) : null;
  const pageSize = Number(loadMore.getAttribute("page-size"));

  if (!list || !pageSize) {
    return;
  }

  if (list._styleFillRunning) {
    return;
  }

  list._styleFillRunning = true;
  let history = options.updateHistory === true;
  const seen = new Set();

  try {
    let current = loadMore;

    for (let guard = 0; guard < 20 && current?.isConnected; guard += 1) {
      const liveList = document.getElementById(listId);

      if (!liveList) {
        break;
      }

      if (!liveList.hasAttribute("data-style-shown-limit")) {
        liveList.setAttribute("data-style-shown-limit", String(pageSize));
      }

      const limit = Number(liveList.getAttribute("data-style-shown-limit"));
      applyShownLimit(liveList, limit);

      const liveMore = loadMoreForList(listId);
      if (liveMore) {
        syncViewingProgress(liveMore, liveList);
      }

      if (
        liveMore &&
        !liveMore.hasAttribute("next-url") &&
        heldCardCount(liveList) === 0
      ) {
        liveMore.remove();
        break;
      }

      if (
        shownCardCount(liveList) >= limit ||
        !liveMore?.hasAttribute("next-url")
      ) {
        liveMore?._syncLoadMoreCount();
        break;
      }

      const nextUrl = liveMore.getAttribute("next-url");
      if (seen.has(nextUrl)) {
        break;
      }
      seen.add(nextUrl);

      const loaded = await liveMore.loadNextPage({ updateHistory: history });
      history = false;

      if (!loaded) {
        break;
      }

      current = loadMoreForList(listId);
    }
  } finally {
    const liveList = document.getElementById(listId);

    if (liveList) {
      liveList._styleFillRunning = false;

      if (liveList._styleFillAgain) {
        liveList._styleFillAgain = false;
        const liveMore = loadMoreForList(listId);
        if (liveMore) {
          fillToLimit(liveMore, { updateHistory: false });
        }
      }
    }
  }
}

async function revealNextBatch(loadMore) {
  const pageSize = Number(loadMore.getAttribute("page-size"));
  const list = document.getElementById(
    loadMore.getAttribute("product-list-id"),
  );

  if (!pageSize || !list) {
    await loadMore.loadNextPage({ updateHistory: true });
    return;
  }

  const limit = Number(list.getAttribute("data-style-shown-limit") || pageSize);
  const shown = shownCardCount(list);
  list.setAttribute(
    "data-style-shown-limit",
    String(Math.max(limit, shown) + pageSize),
  );

  if (list._styleFillRunning) {
    list._styleFillAgain = true;
    return;
  }

  await fillToLimit(loadMore, { updateHistory: true });
}

function appendItems(productList, items) {
  const fragment = document.createDocumentFragment();

  items.forEach((node) => {
    fragment.appendChild(prepareImportedNode(node));
  });

  productList.appendChild(fragment);
}

class CollectionLoadMore extends HTMLElement {
  connectedCallback() {
    if (this._initialized) {
      return;
    }
    this._initialized = true;
    this._busy = false;
    this._activated = false;
    this._pointerStart = null;
    this._pointerMoved = false;

    this._onPointerDownBound = this._onPointerDown.bind(this);
    this._onPointerMoveBound = this._onPointerMove.bind(this);
    this._onPointerUpBound = this._onPointerUp.bind(this);
    this._onPointerCancelBound = this._onPointerCancel.bind(this);
    this._onClickBound = this._onClick.bind(this);
    this._onKeyDownBound = this._onKeyDown.bind(this);

    this._bindButton();
    this._prefetchNext();

    const productListId = this.getAttribute("product-list-id");
    const productList = productListId
      ? document.getElementById(productListId)
      : null;

    if (productList) {
      syncViewingProgress(this, productList);
      ensureVisibleTotal(this, productList);
    }

    this._syncLoadMoreCount();
    fillToLimit(this, { updateHistory: false });
  }

  disconnectedCallback() {
    this._unbindButton();
    this._onPointerDownBound = null;
    this._onPointerMoveBound = null;
    this._onPointerUpBound = null;
    this._onPointerCancelBound = null;
    this._onClickBound = null;
    this._onKeyDownBound = null;
    this._pointerStart = null;
    this._initialized = false;
  }

  _bindButton() {
    const button = this.querySelector(
      "button.collection__button, button.button",
    );
    if (!button || button === this._button) {
      return;
    }

    this._unbindButton();
    this._button = button;
    button.addEventListener("pointerdown", this._onPointerDownBound);
    button.addEventListener("pointermove", this._onPointerMoveBound, {
      passive: true,
    });
    button.addEventListener("pointerup", this._onPointerUpBound);
    button.addEventListener("pointercancel", this._onPointerCancelBound);
    button.addEventListener("click", this._onClickBound);
    button.addEventListener("keydown", this._onKeyDownBound);
    button.addEventListener("mouseenter", () => this._prefetchNext(), {
      passive: true,
    });
  }

  _unbindButton() {
    if (!this._button) {
      return;
    }

    this._button.removeEventListener("pointerdown", this._onPointerDownBound);
    this._button.removeEventListener("pointermove", this._onPointerMoveBound);
    this._button.removeEventListener("pointerup", this._onPointerUpBound);
    this._button.removeEventListener(
      "pointercancel",
      this._onPointerCancelBound,
    );
    this._button.removeEventListener("click", this._onClickBound);
    this._button.removeEventListener("keydown", this._onKeyDownBound);
    this._button = null;
  }

  _resetPointer() {
    this._pointerStart = null;
    this._pointerMoved = false;
  }

  _onPointerDown(event) {
    if (event.button !== 0) {
      return;
    }

    this._activated = false;
    this._pointerStart = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
    this._pointerMoved = false;
  }

  _onPointerMove(event) {
    if (!this._pointerStart || event.pointerId !== this._pointerStart.id) {
      return;
    }

    const deltaX = Math.abs(event.clientX - this._pointerStart.x);
    const deltaY = Math.abs(event.clientY - this._pointerStart.y);

    if (
      deltaX > POINTER_CLICK_TOLERANCE_PX ||
      deltaY > POINTER_CLICK_TOLERANCE_PX
    ) {
      this._pointerMoved = true;
    }
  }

  _onPointerCancel() {
    if (this._pointerMoved) {
      this._resetPointer();
    }
  }

  _hasMoreToShow() {
    if (this.hasAttribute("next-url")) {
      return true;
    }

    const list = document.getElementById(this.getAttribute("product-list-id"));
    return !!list && heldCardCount(list) > 0;
  }

  _isValidTap(event) {
    if (
      this._busy ||
      !this._hasMoreToShow() ||
      !this._pointerStart ||
      this._pointerMoved
    ) {
      return false;
    }

    if (event.type !== "click" && event.pointerId !== this._pointerStart.id) {
      return false;
    }

    if (typeof event.clientX !== "number") {
      return true;
    }

    const deltaX = Math.abs(event.clientX - this._pointerStart.x);
    const deltaY = Math.abs(event.clientY - this._pointerStart.y);

    return (
      deltaX <= POINTER_CLICK_TOLERANCE_PX &&
      deltaY <= POINTER_CLICK_TOLERANCE_PX
    );
  }

  async _activate(event) {
    event.preventDefault();
    this._activated = true;
    this._resetPointer();
    await revealNextBatch(this);
  }

  async _onPointerUp(event) {
    if (!this._isValidTap(event)) {
      return;
    }

    await this._activate(event);
  }

  async _onClick(event) {
    if (this._activated) {
      event.preventDefault();
      return;
    }

    if (!this._isValidTap(event)) {
      return;
    }

    await this._activate(event);
  }

  async _onKeyDown(event) {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    if (this._busy || !this._hasMoreToShow()) {
      return;
    }

    event.preventDefault();
    await revealNextBatch(this);
  }

  _prefetchNext() {
    if (!this.hasAttribute("next-url")) {
      return;
    }
    fetchPageHtml(buildFetchUrl(this)).catch(() => {});
  }

  _visibleCardCount(productList) {
    return visibleCardCount(productList);
  }

  _syncLoadMoreCount() {
    syncLoadMoreCount(this).catch((error) => {
      console.error("[collection-load-more]", error);
    });
  }

  _setLoading(isLoading) {
    if (isLoading) {
      this.setAttribute("aria-busy", "true");
    } else {
      this.removeAttribute("aria-busy");
    }
  }

  loadNextPage(options = {}) {
    return this._loadNext(options);
  }

  async _loadNext(options = {}) {
    const productListId = this.getAttribute("product-list-id");
    const productList = document.getElementById(productListId);
    const pageUrl = this.getAttribute("next-url");
    const fetchUrl = buildFetchUrl(this);
    const updateHistory = options.updateHistory !== false;

    if (this._busy || !productList || !pageUrl) {
      return false;
    }

    this._busy = true;
    this._setLoading(true);
    await waitForPaint();

    try {
      const html = await fetchPageHtml(fetchUrl);
      const parsed = parseResponse(this, html);

      if (!parsed) {
        throw new Error("Missing products in response");
      }

      appendItems(productList, parsed.items);
      const shownLimit = Number(
        productList.getAttribute("data-style-shown-limit") || "0",
      );
      if (shownLimit > 0) {
        applyShownLimit(productList, shownLimit);
      }
      if (updateHistory) {
        history.pushState("", "", pageUrl);
      }

      if (parsed.nextLoadMore?.hasAttribute("next-url")) {
        this.replaceWith(document.importNode(parsed.nextLoadMore, true));
      } else if (heldCardCount(productList) > 0) {
        this.removeAttribute("next-url");
        this._busy = false;
        this._setLoading(false);
        publishRealTotal(productList, countProductCards(productList.children));
      } else if (parsed.endButton) {
        publishRealTotal(productList, countProductCards(productList.children));
        this.replaceWith(document.importNode(parsed.endButton, true));
      } else {
        publishRealTotal(productList, countProductCards(productList.children));
        this.remove();
      }

      return true;
    } catch (error) {
      console.error("[collection-load-more]", error);
      this._setLoading(false);
      this._busy = false;
      return false;
    }
  }
}

if (!customElements.get("collection-load-more")) {
  customElements.define("collection-load-more", CollectionLoadMore);
}
